import React, { useState } from 'react';
import { Loader2, EyeOff, Eye } from 'lucide-react';
import { LoginFormData, AuthError } from '../types/auth';

const inputClass =
  'block w-full rounded-lg border border-[#E2E5EA] bg-white px-3 py-2 text-sm text-text-grey placeholder:text-heritage-grey hover:border-[#cfd4dc] focus:border-blueprint-blue focus:outline-none disabled:opacity-60';

const primaryButtonClass =
  'w-full rounded-lg border border-transparent bg-hero-red py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-hero-red-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-hero-red focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 flex items-center justify-center';

interface LoginFormProps {
  formData: LoginFormData;
  isLoading: boolean;
  error: AuthError | null;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: React.FormEvent) => Promise<void>;
  otpSent?: boolean;
  otp?: string;
  setOtp?: (otp: string) => void;
  otpVerified?: boolean;
  isVerifying?: boolean;
  timer?: number;
  canResend?: boolean;
  onResendOtp?: () => Promise<void>;
  onVerifyOtp?: () => Promise<void>;
  onCancelOtp?: () => void;
  resendMessage?: string | null;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  formData,
  isLoading,
  error,
  onInputChange,
  onSubmit,
  otpSent = false,
  otp = '',
  setOtp,
  otpVerified = false,
  isVerifying = false,
  timer = 0,
  canResend = true,
  onResendOtp,
  onVerifyOtp,
  onCancelOtp,
  resendMessage = null,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const handleOtpFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (onVerifyOtp) {
      await onVerifyOtp();
    }
  };

  if (otpSent && !otpVerified) {
    return (
      <form onSubmit={handleOtpFormSubmit} className="space-y-5" noValidate>
        {error && (
          <div
            className="bg-[rgba(196,0,31,0.06)] text-readable-red px-4 py-3 rounded-lg text-sm"
            role="alert"
          >
            {error.message}
          </div>
        )}
        {resendMessage && (
          <div
            className="rounded-lg border border-green-200 bg-green-50 px-3 py-2.5 text-[13px] leading-snug text-green-800"
            role="status"
          >
            {resendMessage}
          </div>
        )}

        <div className="space-y-1">
          <label className="block text-sm font-medium text-text-grey" htmlFor="otp-code">
            Verification code
          </label>
          <p className="text-sm text-heritage-grey mb-1">
            We&apos;ve sent a 6-digit code to {formData.email}
          </p>
          <input
            id="otp-code"
            type="text"
            inputMode="numeric"
            placeholder="000000"
            value={otp}
            onChange={(e) => {
              if (setOtp) {
                setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
              }
            }}
            disabled={isVerifying}
            autoComplete="one-time-code"
            autoFocus
            className={inputClass}
          />
        </div>

        <button
          type="submit"
          disabled={isVerifying || otp.length < 6}
          className={primaryButtonClass}
        >
          {isVerifying ? (
            <>
              <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
              Verifying...
            </>
          ) : (
            'Verify'
          )}
        </button>

        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={onCancelOtp}
            disabled={isVerifying}
            className="text-sm text-heritage-grey hover:text-text-grey transition-colors disabled:opacity-60"
          >
            ← Back to login
          </button>
          <button
            type="button"
            onClick={async () => {
              if (onResendOtp) {
                await onResendOtp();
              }
            }}
            disabled={!canResend || isVerifying}
            className="text-sm font-semibold text-hero-red hover:underline disabled:opacity-60 disabled:no-underline"
          >
            {canResend ? 'Resend code' : `Resend code in ${timer}s`}
          </button>
        </div>
      </form>
    );
  }

  if (otpVerified) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2.5 text-[13px] leading-snug text-green-800 text-center">
        OTP verified successfully. Signing in...
      </div>
    );
  }

  return (
    <form className="space-y-5" onSubmit={onSubmit} noValidate>
      {error && (
        <div
          className="bg-[rgba(196,0,31,0.06)] text-readable-red px-4 py-3 rounded-lg text-sm"
          role="alert"
        >
          {error.message}
        </div>
      )}

      <div className="space-y-1">
        <label className="block text-sm font-medium text-text-grey" htmlFor="email">
          Email address
        </label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          placeholder="you@crce.com"
          value={formData.email}
          onChange={onInputChange}
          disabled={isLoading}
          className={inputClass}
        />
      </div>

      <div className="space-y-1">
        <label className="block text-sm font-medium text-text-grey" htmlFor="password">
          Password
        </label>
        <div className="relative">
          <input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            required
            placeholder="Enter password"
            value={formData.password}
            onChange={onInputChange}
            disabled={isLoading}
            className={`${inputClass} pr-11 [&::-ms-reveal]:hidden [&::-ms-clear]:hidden`}
          />
          <button
            type="button"
            onClick={() => setShowPassword((value) => !value)}
            disabled={isLoading}
            className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-heritage-grey hover:bg-[#EEF0F3] hover:text-text-grey focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:ring-offset-1 disabled:opacity-50"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? (
              <EyeOff className="h-5 w-5 shrink-0" aria-hidden />
            ) : (
              <Eye className="h-5 w-5 shrink-0" aria-hidden />
            )}
          </button>
        </div>
      </div>

      <div className="flex items-center">
        <input
          id="remember-me"
          name="rememberMe"
          type="checkbox"
          className="h-4 w-4 text-hero-red focus:ring-hero-red border-[#E2E5EA] rounded"
          checked={formData.rememberMe}
          onChange={onInputChange}
          disabled={isLoading}
        />
        <label htmlFor="remember-me" className="ml-2 block text-sm text-text-grey">
          Remember me
        </label>
      </div>

      <button type="submit" disabled={isLoading} className={primaryButtonClass}>
        {isLoading ? (
          <>
            <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
            Signing in...
          </>
        ) : (
          'Sign in'
        )}
      </button>
    </form>
  );
};
